/**
 * FocusView - Focus mode with timer, checklist, and notepad.
 *
 * States: idle, planning, active, paused, summary (completed/abandoned)
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Crosshair, Play, Pause, Square, ArrowRight, Check, Plus,
  X, Clock, ChevronDown, ChevronUp, AlertTriangle, GripVertical,
  MessageSquare, Sparkles, Undo2,
} from 'lucide-react';
import { useFocusSession, parseUtcTimestamp } from '../hooks/useFocusSession';
import { useTodos } from '../hooks/useTodos';
import { AIChat } from './ai/AIChat';
import { focusAPI } from '../services/api';
import type { FocusSession, Todo } from '../types';

// Pause discouragement messages
const PAUSE_MESSAGES = [
  "Take a breath, then get back to it.",
  "You've got this. Resume when ready.",
  "Brief pause — don't lose momentum.",
  "Quick break. Your tasks are waiting.",
  "Rest, but don't quit.",
];

const DURATION_PRESETS = [15, 25, 45, 60, 90];

function formatTime(seconds: number): string {
  const abs = Math.abs(seconds);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  const sign = seconds < 0 ? '+' : '';
  return `${sign}${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function openTodoModal(todoId: string, allTodos: Todo[]) {
  const todo = allTodos.find(t => t.id === todoId);
  if (todo) {
    window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
  }
}

// ============================================================================
// Idle state — no active session
// ============================================================================

function IdleState({ onStart, history }: {
  onStart: () => void;
  history: FocusSession[];
}) {
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center h-full py-12">
      <Crosshair className="w-16 h-16 text-orange-400 mb-6" />
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Focus Mode</h2>
      <p className="text-gray-500 dark:text-gray-400 mb-8 text-center max-w-md">
        Select tasks, set a timer, and lock into a focused work session.
      </p>
      <button
        onClick={onStart}
        className="px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-lg font-medium shadow-md"
      >
        Start Focus Session
      </button>

      {history.length > 0 && (
        <div className="mt-8 w-full max-w-lg">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-3"
          >
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Past sessions ({history.length})
          </button>
          {showHistory && (
            <div className="space-y-2">
              {history.map(s => {
                const completedItems = s.items.filter(i => i.is_completed).length;
                const totalItems = s.items.length;
                return (
                  <div key={s.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                    <div>
                      <span className={s.status === 'completed' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>
                        {s.status === 'completed' ? 'Completed' : 'Abandoned'}
                      </span>
                      <span className="text-gray-400 mx-2">-</span>
                      <span className="text-gray-600 dark:text-gray-300">{completedItems}/{totalItems} tasks</span>
                      {s.planned_duration_minutes && (
                        <span className="text-gray-400 ml-2">{s.planned_duration_minutes}m</span>
                      )}
                    </div>
                    <span className="text-gray-400 text-xs">
                      {new Date(parseUtcTimestamp(s.completed_time || s.created_time)).toLocaleDateString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Planning phase
// ============================================================================

function PlanningPhase({ session, focus }: { session: FocusSession; focus: ReturnType<typeof useFocusSession> }) {
  const { allTodos } = useTodos();
  const [searchQuery, setSearchQuery] = useState('');
  const [quickTitle, setQuickTitle] = useState('');
  const [duration, setDuration] = useState<number | null>(session.planned_duration_minutes);
  const [showChat, setShowChat] = useState(false);
  const chatSendRef = useRef<((message: string) => void) | null>(null);

  // Drag state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Filter available todos (incomplete, not already in session)
  const sessionTodoIds = useMemo(() => new Set(session.items.map(i => i.todo_id).filter(Boolean)), [session.items]);
  const availableTodos = useMemo(() => {
    const filtered = allTodos.filter(t =>
      t.status !== 'completed' && t.status !== 'canceled' && !sessionTodoIds.has(t.id)
    );
    if (!searchQuery.trim()) return filtered.slice(0, 20);
    const q = searchQuery.toLowerCase();
    return filtered.filter(t => t.title.toLowerCase().includes(q)).slice(0, 20);
  }, [allTodos, sessionTodoIds, searchQuery]);

  const handleAddTodo = useCallback((todo: Todo) => {
    focus.addItem.mutate({ sessionId: session.id, data: { todo_id: todo.id, title: todo.title } });
  }, [focus.addItem, session.id]);

  const handleAddQuick = useCallback(() => {
    if (!quickTitle.trim()) return;
    focus.addItem.mutate({ sessionId: session.id, data: { title: quickTitle.trim() } });
    setQuickTitle('');
  }, [focus.addItem, session.id, quickTitle]);

  const handleRemoveItem = useCallback((itemId: string) => {
    focus.removeItem.mutate({ sessionId: session.id, itemId });
  }, [focus.removeItem, session.id]);

  const handleSetDuration = useCallback((mins: number | null) => {
    setDuration(mins);
    focus.updateSession.mutate({ id: session.id, data: { planned_duration_minutes: mins } });
  }, [focus.updateSession, session.id]);

  const handleStart = useCallback(() => {
    focus.startSession.mutate(session.id);
  }, [focus.startSession, session.id]);

  const handleAbandon = useCallback(() => {
    if (confirm('Abandon this session?')) {
      focus.abandonSession.mutate(session.id);
    }
  }, [focus.abandonSession, session.id]);

  // Drag-and-drop handlers
  const handleDragStart = useCallback((idx: number) => {
    setDragIdx(idx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback((targetIdx: number) => {
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    // Build new order: move item from dragIdx to targetIdx
    const items = [...session.items];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(targetIdx, 0, moved);
    const newOrder = items.map(i => i.id);
    focus.reorderItems.mutate({ sessionId: session.id, itemIds: newOrder });
    setDragIdx(null);
    setDragOverIdx(null);
  }, [dragIdx, session.items, session.id, focus.reorderItems]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  // "Plan for me" — sends a pre-built message to the AI
  const handlePlanForMe = useCallback(() => {
    setShowChat(true);
    // Give the chat a moment to mount, then send the message
    setTimeout(() => {
      if (chatSendRef.current) {
        const itemList = session.items.length > 0
          ? `I already have these items: ${session.items.map(i => i.title).join(', ')}. `
          : '';
        chatSendRef.current(
          `Help me plan my focus session. ${itemList}Look at my open tasks and suggest what I should focus on, in what order, and for how long.`
        );
      }
    }, 100);
  }, [session.items]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <Crosshair className="w-5 h-5 text-orange-500" />
          Plan Your Session
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePlanForMe}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Plan for me
          </button>
          <button
            onClick={() => setShowChat(!showChat)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              showChat
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Chat
          </button>
          <button
            onClick={handleAbandon}
            className="text-sm text-gray-400 hover:text-red-500 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Task picker */}
          <div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Add Tasks</h3>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm mb-2 outline-none focus:ring-2 focus:ring-orange-500"
            />
            <div className="max-h-60 overflow-y-auto space-y-1 mb-4">
              {availableTodos.map(todo => (
                <button
                  key={todo.id}
                  onClick={() => handleAddTodo(todo)}
                  className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 text-gray-700 dark:text-gray-300 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-3 h-3 flex-shrink-0 text-orange-500" />
                  <span className="truncate">{todo.title}</span>
                  {todo.horizon && (
                    <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{todo.horizon}</span>
                  )}
                </button>
              ))}
              {availableTodos.length === 0 && (
                <p className="text-sm text-gray-400 px-3 py-2">No matching tasks</p>
              )}
            </div>

            {/* Quick add */}
            <div className="flex gap-2">
              <input
                type="text"
                value={quickTitle}
                onChange={e => setQuickTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddQuick(); }}
                placeholder="Quick add item..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                onClick={handleAddQuick}
                disabled={!quickTitle.trim()}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 text-sm disabled:opacity-50 transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          {/* Right: Session config */}
          <div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              Session Items ({session.items.length})
            </h3>
            <div className="space-y-1 mb-6 min-h-[60px]">
              {session.items.length === 0 && (
                <p className="text-sm text-gray-400 py-4 text-center">Add tasks from the left to get started</p>
              )}
              {session.items.map((item, idx) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm transition-all ${
                    dragIdx === idx ? 'opacity-40' : ''
                  } ${dragOverIdx === idx && dragIdx !== idx ? 'ring-2 ring-orange-400' : ''}`}
                >
                  <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0 cursor-grab active:cursor-grabbing" />
                  {item.todo_id ? (
                    <button
                      onClick={() => openTodoModal(item.todo_id!, allTodos)}
                      className="text-gray-700 dark:text-gray-300 truncate flex-1 text-left hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                    >
                      {item.title}
                    </button>
                  ) : (
                    <span className="text-gray-700 dark:text-gray-300 truncate flex-1">{item.title}</span>
                  )}
                  {item.todo_id && (
                    <span className="text-xs text-orange-400 flex-shrink-0">linked</span>
                  )}
                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* Duration picker */}
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Duration</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {DURATION_PRESETS.map(mins => (
                <button
                  key={mins}
                  onClick={() => handleSetDuration(mins)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    duration === mins
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {mins}m
                </button>
              ))}
              <button
                onClick={() => handleSetDuration(null)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  duration === null
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                No limit
              </button>
            </div>

            {/* Start button */}
            <button
              onClick={handleStart}
              disabled={session.items.length === 0}
              className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium text-lg disabled:opacity-50 disabled:hover:bg-orange-500 flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Start Focusing
            </button>
          </div>
        </div>

        {/* AI Chat panel */}
        {showChat && (
          <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" style={{ height: '400px' }}>
            <AIChat
              compact
              className="h-full"
              focusSessionId={session.id}
              onSendMessageRef={chatSendRef}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Active / Paused phase
// ============================================================================

function ActivePhase({ session, focus }: { session: FocusSession; focus: ReturnType<typeof useFocusSession> }) {
  const { allTodos } = useTodos();
  const { remainingSeconds, pauseElapsedSeconds, isOvertime } = focus;
  const [showNotepad, setShowNotepad] = useState(false);
  const isPaused = session.status === 'paused';

  const pauseMessage = useMemo(() => {
    if (!isPaused) return '';
    return PAUSE_MESSAGES[Math.floor(pauseElapsedSeconds / 10) % PAUSE_MESSAGES.length];
  }, [isPaused, pauseElapsedSeconds]);

  const handlePause = useCallback(() => { focus.pauseSession.mutate(session.id); }, [focus.pauseSession, session.id]);
  const handleResume = useCallback(() => { focus.resumeSession.mutate(session.id); }, [focus.resumeSession, session.id]);
  const handleComplete = useCallback(() => { focus.completeSession.mutate(session.id); }, [focus.completeSession, session.id]);
  const handleAbandon = useCallback(() => {
    if (confirm('Abandon this session? Progress will be saved.')) {
      focus.abandonSession.mutate(session.id);
    }
  }, [focus.abandonSession, session.id]);

  const handleCompleteItem = useCallback((itemId: string) => {
    focus.completeItem.mutate({ sessionId: session.id, itemId });
  }, [focus.completeItem, session.id]);

  const handleSkipItem = useCallback((itemId: string) => {
    focus.skipItem.mutate({ sessionId: session.id, itemId });
  }, [focus.skipItem, session.id]);

  const handleUncompleteItem = useCallback((itemId: string) => {
    focus.uncompleteItem.mutate({ sessionId: session.id, itemId });
  }, [focus.uncompleteItem, session.id]);

  const handleUnskipItem = useCallback((itemId: string) => {
    focus.unskipItem.mutate({ sessionId: session.id, itemId });
  }, [focus.unskipItem, session.id]);

  const completedCount = session.items.filter(i => i.is_completed).length;
  const skippedCount = session.items.filter(i => i.is_skipped).length;
  const totalCount = session.items.length;

  return (
    <div className="flex flex-col h-full">
      {/* Timer header */}
      <div className={`flex flex-col items-center py-6 border-b transition-colors ${
        isPaused
          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
          : isOvertime
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}>
        {/* Timer display */}
        {remainingSeconds !== null && (
          <div className={`text-5xl font-mono font-bold mb-2 ${
            isPaused
              ? 'text-amber-600 dark:text-amber-400'
              : isOvertime
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-800 dark:text-gray-200'
          }`}>
            {formatTime(remainingSeconds)}
          </div>
        )}
        {remainingSeconds === null && (
          <div className="text-5xl font-mono font-bold mb-2 text-gray-800 dark:text-gray-200">
            <Clock className="w-12 h-12 inline" />
          </div>
        )}

        {/* Pause info */}
        {isPaused && (
          <div className="text-center mb-2">
            <p className="text-amber-600 dark:text-amber-400 font-medium text-sm">
              PAUSED - {formatDuration(pauseElapsedSeconds)}
            </p>
            <p className="text-amber-500 dark:text-amber-500 text-xs mt-1 italic">{pauseMessage}</p>
            {pauseElapsedSeconds > session.max_pause_seconds && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1 justify-center">
                <AlertTriangle className="w-3 h-3" />
                Max pause time exceeded
              </p>
            )}
          </div>
        )}

        {/* Progress */}
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          {completedCount}/{totalCount} completed
          {skippedCount > 0 && `, ${skippedCount} skipped`}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          {isPaused ? (
            <button
              onClick={handleResume}
              className="flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
            >
              <Play className="w-4 h-4" />
              Resume
            </button>
          ) : (
            <button
              onClick={handlePause}
              className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
            >
              <Pause className="w-4 h-4" />
              Pause
            </button>
          )}
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 px-6 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
          >
            <Square className="w-4 h-4" />
            Finish
          </button>
        </div>
      </div>

      {/* Checklist */}
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-lg mx-auto space-y-2">
          {session.items.map((item, idx) => {
            const isCurrent = !item.is_completed && !item.is_skipped &&
              session.items.findIndex(i => !i.is_completed && !i.is_skipped) === idx;

            return (
              <div
                key={item.id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  item.is_completed
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : item.is_skipped
                      ? 'bg-gray-50 dark:bg-gray-800 opacity-50'
                      : isCurrent
                        ? 'bg-orange-50 dark:bg-orange-900/20 ring-2 ring-orange-300 dark:ring-orange-700'
                        : 'bg-white dark:bg-gray-700'
                }`}
              >
                {item.todo_id ? (
                  <button
                    onClick={() => openTodoModal(item.todo_id!, allTodos)}
                    className={`text-sm flex-1 text-left transition-colors ${
                      item.is_completed
                        ? 'line-through text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300'
                        : item.is_skipped
                          ? 'line-through text-gray-400 hover:text-gray-500'
                          : 'text-gray-800 dark:text-gray-200 hover:text-orange-600 dark:hover:text-orange-400'
                    }`}
                  >
                    {item.title}
                  </button>
                ) : (
                  <span className={`text-sm flex-1 ${
                    item.is_completed
                      ? 'line-through text-green-600 dark:text-green-400'
                      : item.is_skipped
                        ? 'line-through text-gray-400'
                        : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {item.title}
                  </span>
                )}

                {!item.is_completed && !item.is_skipped && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleCompleteItem(item.id)}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-700 transition-colors text-xs font-medium"
                      title="Mark done"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Done
                    </button>
                    <button
                      onClick={() => handleSkipItem(item.id)}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-gray-100 dark:bg-gray-600 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors text-xs font-medium"
                      title="Skip this item"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                      Skip
                    </button>
                  </div>
                )}
                {item.is_completed && (
                  <button
                    onClick={() => handleUncompleteItem(item.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-green-500 hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors text-xs flex-shrink-0"
                    title="Undo completion"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <Undo2 className="w-3 h-3" />
                  </button>
                )}
                {item.is_skipped && (
                  <button
                    onClick={() => handleUnskipItem(item.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors text-xs flex-shrink-0"
                    title="Undo skip"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                    <Undo2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Notepad */}
        <div className="max-w-lg mx-auto mt-6">
          <button
            onClick={() => setShowNotepad(!showNotepad)}
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
          >
            {showNotepad ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Notepad
          </button>
          {showNotepad && (
            <textarea
              value={focus.localNotepad}
              onChange={e => focus.updateNotepad(e.target.value)}
              placeholder="Capture ideas, notes, or thoughts..."
              className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm outline-none focus:ring-2 focus:ring-orange-500 resize-y"
            />
          )}
        </div>

        {/* Abandon link */}
        <div className="max-w-lg mx-auto mt-4 text-center">
          <button
            onClick={handleAbandon}
            className="text-sm text-gray-400 hover:text-red-500 transition-colors"
          >
            Abandon session
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Summary phase (completed/abandoned)
// ============================================================================

function SummaryPhase({ session, onNewSession, onClose }: {
  session: FocusSession;
  onNewSession: () => void;
  onClose: () => void;
}) {
  const completedItems = session.items.filter(i => i.is_completed);
  const skippedItems = session.items.filter(i => i.is_skipped);
  const remainingItems = session.items.filter(i => !i.is_completed && !i.is_skipped);

  // Calculate actual duration
  let actualMinutes: number | null = null;
  if (session.started_time && session.completed_time) {
    const startMs = parseUtcTimestamp(session.started_time);
    const endMs = parseUtcTimestamp(session.completed_time);
    actualMinutes = Math.round((endMs - startMs - session.total_pause_seconds * 1000) / 60000);
  }

  const isCompleted = session.status === 'completed';

  return (
    <div className="flex flex-col items-center py-12 px-4">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
        isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
      }`}>
        {isCompleted
          ? <Check className="w-8 h-8 text-green-500" />
          : <X className="w-8 h-8 text-gray-400" />
        }
      </div>

      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-1">
        {isCompleted ? 'Session Complete' : 'Session Abandoned'}
      </h2>

      {/* Stats */}
      <div className="flex gap-6 mt-4 mb-6 text-center">
        <div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{completedItems.length}</div>
          <div className="text-xs text-gray-500">Completed</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-400">{skippedItems.length}</div>
          <div className="text-xs text-gray-500">Skipped</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-orange-500">{remainingItems.length}</div>
          <div className="text-xs text-gray-500">Remaining</div>
        </div>
      </div>

      {/* Duration comparison */}
      {(session.planned_duration_minutes || actualMinutes) && (
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {session.planned_duration_minutes && <span>Planned: {session.planned_duration_minutes}m</span>}
          {session.planned_duration_minutes && actualMinutes && <span className="mx-2">|</span>}
          {actualMinutes !== null && <span>Actual: {actualMinutes}m</span>}
          {session.total_pause_seconds > 0 && (
            <span className="ml-2">(paused {formatDuration(session.total_pause_seconds)})</span>
          )}
        </div>
      )}

      {/* Notepad contents */}
      {session.notepad && (
        <div className="w-full max-w-md mb-6">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Session Notes</h3>
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {session.notepad}
          </div>
        </div>
      )}

      {/* Task list */}
      <div className="w-full max-w-md mb-6">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Tasks</h3>
        <div className="space-y-1">
          {session.items.map(item => (
            <div key={item.id} className="flex items-center gap-2 text-sm px-2 py-1">
              {item.is_completed && <Check className="w-3 h-3 text-green-500 flex-shrink-0" />}
              {item.is_skipped && <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />}
              {!item.is_completed && !item.is_skipped && <span className="w-3 h-3 flex-shrink-0" />}
              <span className={
                item.is_completed ? 'text-green-600 dark:text-green-400 line-through' :
                item.is_skipped ? 'text-gray-400 line-through' :
                'text-gray-600 dark:text-gray-300'
              }>
                {item.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onNewSession}
          className="px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
        >
          New Session
        </button>
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main FocusView
// ============================================================================

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="mx-4 mt-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between gap-2">
      <span className="text-sm text-red-700 dark:text-red-300">{message}</span>
      <button onClick={onDismiss} className="text-red-500 hover:text-red-700 flex-shrink-0">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function FocusView() {
  const focus = useFocusSession();
  const { session, isLoading, mutationError, clearError } = focus;
  const [history, setHistory] = useState<FocusSession[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Load history when idle
  const loadHistory = useCallback(async () => {
    if (!historyLoaded) {
      try {
        const h = await focusAPI.getHistory(10);
        setHistory(h);
      } catch {
        // ignore
      }
      setHistoryLoaded(true);
    }
  }, [historyLoaded]);

  // Load history on mount when no active session
  React.useEffect(() => {
    if (!session && !isLoading) loadHistory();
  }, [session, isLoading, loadHistory]);

  const handleStartNew = useCallback(() => {
    focus.createSession.mutate({});
    setHistoryLoaded(false);
  }, [focus.createSession]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) {
    return <IdleState onStart={handleStartNew} history={history} />;
  }

  const errorBanner = mutationError ? <ErrorBanner message={mutationError} onDismiss={clearError} /> : null;

  if (session.status === 'planning') {
    return <>{errorBanner}<PlanningPhase session={session} focus={focus} /></>;
  }

  if (session.status === 'active' || session.status === 'paused') {
    return <>{errorBanner}<ActivePhase session={session} focus={focus} /></>;
  }

  // completed or abandoned
  return (
    <SummaryPhase
      session={session}
      onNewSession={handleStartNew}
      onClose={() => {
        setHistoryLoaded(false);
        // Force refetch to get null active session
        focus.createSession.reset();
      }}
    />
  );
}
